import React, { useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate, useParams } from 'react-router-dom';


interface StyledTabProps {
  label: string;
  subLabel?: string;
  disabled?: boolean;
}
interface TabItem {
  id: string;
  label: string;
  subLabel?: string;
  component: React.ReactNode;
  disabled?: boolean;
}

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
    scrollButtons="auto"
    variant="scrollable"
  />
))(({ theme }) => ({
  position: 'relative',
  // backgroundColor: '#F6F5EF', // No background for the entire tabs box
  '& .MuiTabs-indicator': {
    display: 'none', // Hide the default MUI indicator
  },
}));

const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '12px',
  color: '#333', // Default tab color (dark text)
  minHeight: '34px',
  minWidth: '100px',
  maxWidth: '100%',// Ensure tabs have equal width
  border: `1px solid ${theme.palette.divider}`, // Border around each tab
  // Rounded corners (like in your example)
  marginRight: '0.5rem', // Space between each tab
  backgroundColor: '#fff', // Default background for tabs
  '&.Mui-selected': {
    backgroundColor: '#353C52', // Black background for selected tab
    color: '#fff', // White text for selected tab
    fontWeight: 'bold', // Bold font for selected tab
    borderRadius: '30px 30px 0 30px',
  },
  '&:hover': {
    backgroundColor: '#ddd', // Light hover effect on tabs
    color: '#000', // Change text color on hover
  },
  '&:last-of-type': {
    marginRight: 0, // Remove margin on the last tab
  },
}));

const StyledContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  border: '2px solid rgba(128, 128, 128, .2)', // 1rem solid red border
  borderRadius: 'none', // Optional: add some rounded corners
  // backgroundColor: '#F6F5EF', // Optional: light background for content area
}));

interface CustomizedTabsProps {
  tabs: TabItem[];
  rightComponent?: React.ReactNode; // Optional right component
  baseUrl: string; // Optional base URL for routing, defaults to '/' if not provided  // Define the type for the props of ThemeWrapper
  system: string;
  ID?: string;
  disabled?: boolean;
}

const CustomizedTabs: React.FC<CustomizedTabsProps> = ({ tabs, rightComponent, baseUrl, system, ID }) => {
  const { currentTab } = useParams<{ currentTab: string; }>();
  const navigate = useNavigate();

  // Determine the initial selected tab based on the currentTab param
  const initialTabIndex = tabs.findIndex((tab) => tab.id === currentTab && !tab.disabled); // Only consider enabled tabs
  const [value, setValue] = React.useState(initialTabIndex !== -1 ? initialTabIndex : 0);

  // Update the state if the URL param changes and the tab is not disabled
  useEffect(() => {
    if (initialTabIndex !== -1 && initialTabIndex !== value) {
      setValue(initialTabIndex);
    }
  }, [initialTabIndex, value]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const newTab = tabs[newValue];
    if (!newTab.disabled) {
      setValue(newValue);
      const newTabId = newTab.id;
      if (ID) {
        navigate(`/${system}/${baseUrl}/${newTabId}/${ID}`);
      } else {
        navigate(`/${system}/${baseUrl}/${newTabId}`);
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="grid" alignItems="center" justifyContent="space-between">
        <StyledTabs
          value={value}
          onChange={handleChange}
          aria-label="styled tabs with routing params"
          sx={{ flexGrow: 1 }}
        >
          {tabs.map((tab) => (
            <StyledTab key={tab.id} label={tab.label} disabled={tab.disabled} />
          ))}
        </StyledTabs>
        {rightComponent && <Box sx={{ ml: 2 }}>{rightComponent}</Box>}
      </Box>
      <StyledContentBox>{tabs[value]?.component}</StyledContentBox>
    </Box>
  );
};

export default CustomizedTabs;
